/**
 * One page box: the 's' placeholder (decoding="sync", never removed), the large-tier image(s)
 * stacked above it (mounted by the decode scheduler), the annotation overlay slot, connector pins
 * and the jump pulse. In two-up page mode the pages of other spreads stay mounted but hidden
 * (visibility: hidden — no paint, no hit testing; their large images can still be pre-decoded).
 */
import { memo, useLayoutEffect, useRef } from 'react';
import { useT } from '../i18n';
import type { LargeImgState } from './decodeScheduler';
import type { PageBox } from './layout';
import { ConnectorPinButton, PulseRing } from './Pins';
import type { ConnectorPin, RenderPageOverlay, ViewerMode } from './types';

export interface PulseState {
  key: number;
  page: number;
  x: number;
  y: number;
}

function LargeImage({ url, visible, register }: { url: string; visible: boolean; register: (url: string, el: HTMLImageElement | null) => void }) {
  const ref = useRef<HTMLImageElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    register(url, el);
    return () => register(url, null);
  }, [url, register]);
  return (
    <img
      ref={ref}
      src={url}
      alt=""
      decoding="async"
      draggable={false}
      className="no-select pointer-events-none absolute inset-0 h-full w-full"
      style={{ opacity: visible ? 1 : 0 }}
    />
  );
}

export interface PageViewProps {
  index: number;
  box: PageBox;
  sUrl: string | null;
  large: readonly LargeImgState[];
  pins: readonly ConnectorPin[] | undefined;
  pulse: PulseState | null;
  mode: ViewerMode;
  /** Pins ignore taps (edit mode, except while connectors can be selected). */
  pinsInert: boolean;
  /** touch-action of the page box in edit mode. */
  editTouchAction: 'none' | 'pan-x pan-y';
  /** Two-up page mode: the page belongs to another spread. */
  hidden?: boolean;
  showOverlay: boolean;
  renderPageOverlay: RenderPageOverlay | undefined;
  onPin: (pin: ConnectorPin) => void;
  onPulseDone: (key: number) => void;
  registerElement: (url: string, el: HTMLImageElement | null) => void;
}

export const PageView = memo(function PageView({
  index,
  box,
  sUrl,
  large,
  pins,
  pulse,
  mode,
  pinsInert,
  editTouchAction,
  hidden = false,
  showOverlay,
  renderPageOverlay,
  onPin,
  onPulseDone,
  registerElement,
}: PageViewProps) {
  const t = useT();
  const edit = mode === 'edit';
  return (
    <div
      data-page={index}
      role="group"
      aria-label={t('viewer.page_label', { page: index + 1 })}
      aria-hidden={hidden || undefined}
      // Edit mode: no text selection, magnifier or callout on a long press (it selects an own mark).
      className={edit ? 'no-select absolute bg-white shadow-[0_1px_4px_rgb(0_0_0/0.18)]' : 'absolute bg-white shadow-[0_1px_4px_rgb(0_0_0/0.18)]'}
      style={{
        top: box.top,
        left: box.left,
        width: box.width,
        height: box.height,
        touchAction: edit ? editTouchAction : undefined,
        visibility: hidden ? 'hidden' : undefined,
      }}
    >
      {sUrl ? (
        <img src={sUrl} alt="" decoding="sync" draggable={false} className="no-select pointer-events-none absolute inset-0 h-full w-full" />
      ) : (
        <div className="absolute inset-0 animate-pulse bg-[#eef0f5]" aria-hidden />
      )}
      {large.map((l) => (
        <LargeImage key={l.url} url={l.url} visible={l.visible} register={registerElement} />
      ))}
      {showOverlay && renderPageOverlay ? (
        <div className="absolute inset-0" style={{ pointerEvents: edit ? 'auto' : 'none' }}>
          {renderPageOverlay(index, { w: box.width, h: box.height })}
        </div>
      ) : null}
      {pins?.map((p) => (
        <ConnectorPinButton key={p.id} pin={p} pagePins={pins} boxW={box.width} boxH={box.height} inert={pinsInert} onActivate={onPin} />
      ))}
      {pulse ? <PulseRing key={pulse.key} x={pulse.x} y={pulse.y} onDone={() => onPulseDone(pulse.key)} /> : null}
    </div>
  );
});
